import { forwardRef, memo, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { Input, Radio, Spin, Space, Form, TreeSelect, ModalProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { getSpuClass } from '@knowledge/service/api/single';
import { get, find, some } from 'lodash-es';
import { match, when } from 'ts-pattern';
import Empty from 'components/Empty';
import { getMultiLangValue } from '@knowledge/common/hooks/useKnowledgeName';
import { cx } from '@emotion/css';
import { JustRightHeightModal } from 'components/styled/scoped';
import { Formik } from 'formik';
import FormField from 'components/FormField';
import { useCreateSingleClass, useDomainList } from '@knowledge/knowledges/singleManage/connect';
import useModal from 'hooks/useModal';
import Highlighter from 'react-highlight-words';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';

const safeParse = (str: string) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    return {};
  }
};
export type RefProps = {
  refetch: () => Promise<void>;
};
export const SpuClassListSelect = memo(
  forwardRef<
    RefProps,
    {
      value?: Knowledge.SingleClass.Value | undefined;
      onChange?: (value: Knowledge.SingleClass.Value | undefined) => void;
    }
  >((props, ref) => {
    const { value, onChange } = props;
    const { t } = useTranslate();
    const [searchValue, setSearchValue] = useState('');
    const { data, isFetching, isInitialLoading, refetch } = useQuery({
      queryKey: ['getSpuClass', searchValue] as const,
      queryFn: ({ queryKey }) => {
        return getSpuClass({
          spuClassName: get(queryKey, '1', ''),
        });
      },
      refetchOnMount: true,
    });
    useEffect(() => {
      if (!find(data, { id: value?.id })) {
        onChange?.(undefined);
      }
    }, [data, value, onChange]);
    const { hide, visible, show } = useModal();
    const lang = useSystemLanguageValue();

    useImperativeHandle(ref, () => {
      return {
        refetch: () => {
          return refetch().then((res) => {
            const target = find(res.data, { id: value?.id });
            if (target) {
              onChange?.(target);
            }
          });
        },
      };
    });

    return (
      <div className="flex flex-col h-full">
        {visible && (
          <NewSingleClassModal list={data} onRefresh={refetch} visible={visible} onCancel={hide} />
        )}
        <div className="flex-shrink-0 border-0 border-b border-solid border-[var(--fett-border-default)] px-[12px]">
          <div className="flex items-center justify-between py-[12px]">
            <div className="font-bold">{t('single_choose_self')}</div>
            <div
              className="cursor-pointer text-[var(--fett-text-secondary)]"
              onClick={() => {
                show();
              }}
            >
              +{t('single_btn_add')}
            </div>
          </div>
          <Input.Search
            allowClear
            loading={isFetching}
            className="mb-[13px]"
            placeholder={t('请输入单品名称搜索')}
            onSearch={(value) => {
              setSearchValue(value);
            }}
          />
        </div>
        <div className="flex-grow overflow-x-hidden overflow-y-auto px-[12px] pt-[12px]">
          {match([isInitialLoading, isFetching, data])
            .with([true, when(() => true), when(() => true)], () => (
              <div className="h-full flex items-center">
                <div className="inline-block m-auto">
                  <Spin />
                </div>
              </div>
            ))
            .with([false, false, when((data) => !data || get(data, 'length') === 0)], () => {
              return (
                <div className="flex flex-col h-full items-center justify-center">
                  <Empty />
                </div>
              );
            })
            .with([false, false, when((data) => get(data, 'length', 0) > 0)], () => {
              return (
                <div>
                  <Radio.Group className="w-full" value={value?.id}>
                    <Space direction="vertical" className="w-full">
                      {data?.map((item) => {
                        return (
                          <div
                            className={cx(
                              'flex border border-solid border-[var(--fett-border-strong)] p-[10px] rounded-[4px] text-[14px] cursor-pointer flex items-center',
                              {
                                'border-[var(--fett-scenario-accent-text)]': item.id === value?.id,
                              },
                            )}
                            onClick={() => {
                              onChange?.(item);
                            }}
                          >
                            <Radio value={item.id}></Radio>
                            <div className="line-clamp-2">
                              <Highlighter
                                searchWords={[searchValue]}
                                textToHighlight={getMultiLangValue(
                                  item.nameMultiLang,
                                  lang,
                                  item.name,
                                )}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </Space>
                  </Radio.Group>
                </div>
              );
            })
            .otherwise(() => null)}
        </div>
      </div>
    );
  }),
);

SpuClassListSelect.displayName = 'ItemSelectV5SpuClassListSelect';

type IV = {
  type: 'OFFICIAL' | 'CUSTOM';
  spuClassId: number | undefined;
  name: string;
};
interface NewSingleClassModalProps extends ModalProps {
  list?: Knowledge.SingleClass.Value[];
  onRefresh: () => void;
}
const NewSingleClassModal = memo((props: NewSingleClassModalProps) => {
  const { t } = useTranslate();
  const { list, onRefresh, onCancel, ...restProps } = props;
  const { data: domainList, loading: domainLoading } = useDomainList(true, list);
  const verifyRepeat = (value: string) => {
    return some(list, (item) => {
      const multiLang = safeParse(item.nameMultiLang);
      return Object.values(multiLang).includes(value) || item.name === value;
    });
  };
  const { mutateAsync } = useCreateSingleClass();
  return (
    <Formik<IV>
      initialValues={{
        type: 'OFFICIAL',
        spuClassId: undefined,
        name: '',
      }}
      onSubmit={(values) => {
        return mutateAsync(values as any).then(() => {
          onCancel?.(null!);
          onRefresh();
        });
      }}
    >
      {({ values, submitForm, isSubmitting }) => {
        return (
          <JustRightHeightModal
            width={480}
            title={t('single_btn_add')}
            visible
            zIndex={99998}
            onCancel={onCancel}
            onOk={() => {
              submitForm();
            }}
            okText={t('marketing.保存')}
            {...restProps}
            okButtonProps={{
              loading: isSubmitting,
            }}
          >
            <Form layout="vertical">
              <FormField name="type" label={t('single_table_col_type')} required>
                {({ field }) => {
                  return (
                    <Radio.Group {...field}>
                      <Radio value="OFFICIAL">{t('选择已有官方类型')}</Radio>
                      <Radio value="CUSTOM">{t('自定义类型')}</Radio>
                    </Radio.Group>
                  );
                }}
              </FormField>

              {values.type === 'OFFICIAL' && (
                <FormField
                  name="spuClassId"
                  label={t('knowledge.单品')}
                  required
                  validate={(value) => {
                    if (!value) {
                      return t('single_name_required');
                    }
                  }}
                >
                  {({ field }) => {
                    return (
                      <TreeSelect
                        loading={domainLoading}
                        showSearch
                        filterTreeNode={(input, treeNode) => {
                          return (treeNode?.title as any)?.indexOf(input) > -1;
                        }}
                        {...field}
                        treeData={domainList}
                        dropdownStyle={{
                          zIndex: 99999,
                        }}
                        className="!min-w-[240px]"
                      ></TreeSelect>
                    );
                  }}
                </FormField>
              )}

              {values.type === 'CUSTOM' && (
                <FormField
                  name="name"
                  label={t('single_table_col_name')}
                  required
                  validate={(value) => {
                    if (!value) {
                      return t('single_name_required');
                    }
                    if (verifyRepeat(value)) {
                      return t('single_name_repeat');
                    }
                  }}
                >
                  {({ field }) => {
                    return (
                      <Input
                        maxLength={60}
                        showCount
                        {...field}
                        className="!min-w-[240px]"
                        placeholder={t('trade.请输入')}
                      />
                    );
                  }}
                </FormField>
              )}
            </Form>
          </JustRightHeightModal>
        );
      }}
    </Formik>
  );
});

NewSingleClassModal.displayName = 'NewSingleClassModal';
